import React, { FC, useCallback } from 'react'
import cx from 'classnames'
import { getInitials } from 'utils/formatting'
import { CalendarVendorContact } from 'pages/_serverRendered/VendorSiteVisit/types'
import CoreInput from 'components/Core/CoreInput'
import { FancyOption } from 'components/Core/CoreInput/FancySelect'

const computeInitialVendorContactEmail = (
  vendorContact: CalendarVendorContact | null,
  vendorContacts: CalendarVendorContact[]
) => {
  if (vendorContact) {
    if (!vendorContact.ooo) {
      return vendorContact.email || undefined
    } else {
      const backupContact = vendorContacts.find((e) => e.id == vendorContact.backup_vendor_contact_id)
      if (backupContact) {
        if (!backupContact.ooo) {
          return backupContact.email || undefined
        } else {
          // note we don't follow backups recursively. If first is also ooo, just pick any available as default
          const firstAvailableContact = vendorContacts.filter((e) => !e.ooo)[0]
          return firstAvailableContact ? firstAvailableContact.email || undefined : undefined
        }
      }
    }
  }
}

interface VendorContactSelectProps {
  name?: string
  vendorContact: CalendarVendorContact | null
  setVendorContact: (vendorContact: CalendarVendorContact | null) => void
  vendorContacts: CalendarVendorContact[]
  disabled?: boolean
  className?: string
}

const VendorContactSelect: FC<VendorContactSelectProps> = ({
  name,
  vendorContact,
  setVendorContact,
  vendorContacts,
  disabled,
  className,
}) => {
  const initialValue = computeInitialVendorContactEmail(vendorContact, vendorContacts)

  const handleChangeValue = useCallback(
    (email: string) => {
      const newVendorContact = vendorContacts.find((e) => e.email == email) || null
      setVendorContact(newVendorContact)
    },
    [setVendorContact, vendorContacts]
  )

  const renderOption = useCallback(
    (e: FancyOption<CalendarVendorContact, string | undefined>) => {
      const backupName =
        e.payload.ooo && e.payload.backup_vendor_contact_id
          ? vendorContacts.find((x) => x.id == e.payload.backup_vendor_contact_id)?.full_name
          : null
      return (
        <div className="tw:flex tw:gap-1 tw:items-center">
          <div className="tw:shrink-0">
            <div className="tw:rounded-full tw:border-2 tw:border-neutrals-300">
              {/* {e.payload.pic_url ? (
                <img
                  alt={e.payload.full_name}
                  src={e.payload.pic_url}
                  className="tw:border-2 tw:border-white tw:rounded-full tw:size-6 tw:object-cover"
                />
              ) : ( */}
              <div className="tw:border-2 tw:border-white tw:rounded-full tw:flex tw:items-center tw:justify-center tw:pointer-events-none tw:size-6 tw:bg-green-200 tw:text-2xs tw:font-bold">
                {getInitials(e.payload.full_name)}
              </div>
              {/* )} */}
            </div>
          </div>
          <div>
            <div className={cx('tw:flex tw:gap-1 tw:items-center', { 'tw:text-disabled-black': e.disabled })}>
              <div className="tw:text-sm tw:font-semibold tw:text-left">
                {[vendorContact?.id == e.payload.id ? '(You)' : '', e.payload.full_name].join(' ')}
              </div>
              {backupName ? <div className="tw:text-sm tw:text-left">{`(Backup: ${backupName})`}</div> : null}
            </div>
            {e.payload.ooo ? (
              <div className="tw:text-xs tw:font-semibold tw:text-left tw:text-red-600">{'🏖️ Out of office'}</div>
            ) : e.payload.email ? (
              <div className="tw:text-xs tw:font-light tw:text-left">{`<${e.payload.email}>`}</div>
            ) : null}
          </div>
        </div>
      )
    },
    [vendorContact?.id, vendorContacts]
  )

  const options: FancyOption<CalendarVendorContact, string>[] = vendorContacts.map((e) => ({
    disabled: e.ooo,
    value: e.email || '',
    payload: e,
  }))

  return (
    <CoreInput.FancySelect<CalendarVendorContact, string>
      name={name}
      required
      disabled={disabled}
      value={initialValue}
      onChangeValue={handleChangeValue}
      options={options}
      renderOption={renderOption}
      className={className}
    />
  )
}

export default VendorContactSelect
